/**
 * Painel "Colunas": o usuário marca quais colunas vê e as ordena.
 * Salvar persiste na preferência de tabela do usuário (vale em qualquer
 * dispositivo); "Restaurar padrão" volta às seis de fábrica.
 */
import React, { useEffect, useState } from "react";
import Checkbox from "../../../components/Checkbox";
import { FiArrowDown, FiArrowUp } from "react-icons/fi";

import SlideOverPanel from "../../../components/SlideOverPanel";
import { CATALOGO_COLUNAS, COLUNAS_PADRAO, COLUNA_POR_KEY, normalizarColunas } from "./colunas";

export default function ColunasPanel({ open, colunas, onClose, onSalvar }) {
  // Lista ordenada: as visíveis (na ordem escolhida) e depois as ocultas.
  const [ordem, setOrdem] = useState([]);
  const [visiveis, setVisiveis] = useState(() => new Set());
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) return;
    const ativas = normalizarColunas(colunas);
    const resto = CATALOGO_COLUNAS.map((c) => c.key).filter((k) => !ativas.includes(k));
    setOrdem([...ativas, ...resto]);
    setVisiveis(new Set(ativas));
  }, [open, colunas]);

  const alternar = (key) => setVisiveis((prev) => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const mover = (i, delta) => setOrdem((atual) => {
    const j = i + delta;
    if (j < 0 || j >= atual.length) return atual;
    const novo = [...atual];
    [novo[i], novo[j]] = [novo[j], novo[i]];
    return novo;
  });

  const restaurar = () => {
    const resto = CATALOGO_COLUNAS.map((c) => c.key).filter((k) => !COLUNAS_PADRAO.includes(k));
    setOrdem([...COLUNAS_PADRAO, ...resto]);
    setVisiveis(new Set(COLUNAS_PADRAO));
  };

  const salvar = async () => {
    const escolhidas = ordem.filter((k) => visiveis.has(k));
    if (!escolhidas.length) return;
    setSalvando(true);
    try {
      await onSalvar(escolhidas);
    } finally {
      setSalvando(false);
    }
  };

  const nSel = ordem.filter((k) => visiveis.has(k)).length;

  const footer = (
    <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
      <button type="button" className="btn btn--secondary btn--sm" onClick={restaurar}>Restaurar padrão</button>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="btn btn--secondary" onClick={onClose}>Cancelar</button>
        <button type="button" className="btn btn--primary" onClick={salvar} disabled={salvando || nSel === 0}>
          {salvando ? "Salvando…" : `Salvar (${nSel})`}
        </button>
      </div>
    </div>
  );

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Colunas da tabela"
      subtitle="Marque o que quer ver e ordene com as setas. A escolha é sua e vale em qualquer dispositivo."
      footer={footer}
      width="min(90vw, 420px)"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {ordem.map((key, i) => {
          const col = COLUNA_POR_KEY[key];
          if (!col) return null;
          const on = visiveis.has(key);
          return (
            <div
              key={key}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 8px",
                border: "1px solid var(--border)", borderRadius: 8,
                background: on ? "var(--surface)" : "transparent", opacity: on ? 1 : 0.7,
              }}
            >
              <label style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, cursor: "pointer", fontSize: 13 }}>
                <Checkbox checked={on} onChange={() => alternar(key)} aria-label={`Mostrar coluna ${col.label}`} />
                {col.label}
              </label>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => mover(i, -1)} disabled={i === 0} aria-label={`Mover ${col.label} para cima`}>
                <FiArrowUp size={13} />
              </button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => mover(i, 1)} disabled={i === ordem.length - 1} aria-label={`Mover ${col.label} para baixo`}>
                <FiArrowDown size={13} />
              </button>
            </div>
          );
        })}
      </div>
    </SlideOverPanel>
  );
}
